import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TagAPI } from '@/services/tag.api';
import type { Tag, NamedTag } from '@/types';
import { documentTypeTitle } from '@/constants/documents-library/subcategoryTags';

/**
 * The tag vocabulary (AG-249).
 *
 * Documents and requirements carry slugs only; every display name in the UI is
 * resolved through this. Cached hard — the vocabulary is admin-curated and
 * changes rarely, and nearly every document view needs it.
 */
export function useTags(includeInactive = false, enabled = true) {
  return useQuery({
    queryKey: ['tags', includeInactive],
    queryFn: () => TagAPI.getAll(includeInactive),
    // The public share page has no token: it renders NamedTags the endpoint
    // embedded and must not fire an authenticated request that only 401s.
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Slug → display name, plus a resolver that degrades to the slug itself.
 *
 * Falling back to the slug rather than "Okänd" matters: a document may carry a
 * tag that was deactivated after it was uploaded, and showing "vaxtodling"
 * still tells the farmer more than an error would.
 */
export function useTagLookup(options: { enabled?: boolean } = {}): {
  tags: Tag[];
  isLoading: boolean;
  displayName: (slug: string) => string;
  /**
   * A whole tag set as one line, e.g. "Växtodling · Journal · Växtskydd".
   *
   * Accepts bare slugs (the authenticated app, resolved through the vocabulary)
   * and already-named tags (the public share endpoint, which sends names
   * because an auditor cannot fetch the vocabulary).
   */
  label: (tags: readonly (string | NamedTag)[] | null | undefined) => string;
  /**
   * The pärm's title for a document or a group: the former category's display
   * name for a curated tag set ("Säkerhetsdatablad"), else the tag names joined,
   * else "Okategoriserad". Pärm only (David, 2026-09-29) — everywhere else a
   * document shows its tags, via `label` or TagChips.
   */
  typeLabel: (tags: readonly (string | NamedTag)[] | null | undefined) => string;
  /** Tags grouped by dimension, in vocabulary order — how pickers render them. */
  byDimension: Array<{ dimension: string; tags: Tag[] }>;
} {
  const { data, isLoading } = useTags(false, options.enabled ?? true);
  const tags = useMemo(() => data ?? [], [data]);

  const nameBySlug = useMemo(
    () => new Map(tags.map((tag) => [tag.slug, tag.displayName])),
    [tags],
  );

  const byDimension = useMemo(() => {
    const groups = new Map<string, Tag[]>();
    for (const tag of tags) {
      const group = groups.get(tag.dimension) ?? [];
      group.push(tag);
      groups.set(tag.dimension, group);
    }
    // AREA first, then KIND, then TOPIC — broadest to most specific.
    const order = ['AREA', 'KIND', 'TOPIC'];
    return [...groups.entries()]
      .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
      .map(([dimension, group]) => ({ dimension, tags: group }));
  }, [tags]);

  // Stable identities: consumers list these in useMemo/useEffect deps (the pärm
  // groups and sorts every shelf in a memo keyed on typeLabel), so they must
  // only change when the vocabulary does.
  const displayName = useCallback(
    (slug: string): string => nameBySlug.get(slug) ?? slug,
    [nameBySlug],
  );

  const label = useCallback(
    (tags: readonly (string | NamedTag)[] | null | undefined): string =>
      tags && tags.length > 0
        ? tags
            .map((tag) => (typeof tag === 'string' ? displayName(tag) : tag.displayName))
            .join(' · ')
        : 'Okategoriserad',
    [displayName],
  );

  // The title needs no vocabulary, so it works on the share page too.
  const typeLabel = useCallback(
    (tags: readonly (string | NamedTag)[] | null | undefined): string =>
      documentTypeTitle(tags?.map((tag) => (typeof tag === 'string' ? tag : tag.slug))) ??
      label(tags),
    [label],
  );

  return {
    tags,
    isLoading,
    displayName,
    label,
    typeLabel,
    byDimension,
  };
}
